import { type IRoles_ } from "@/shared/services/useRoleManager";
import { lazy } from "react";
import { Navigate, type RouteObject } from "react-router-dom";

const CashboxPage = lazy(() => import("@/pages/cashbox/ui"));
const CashboxMainPage = lazy(() => import("@/pages/cashboxMain"));
const CashboxObjectPage = lazy(() => import("@/pages/cashboxObject"));
const InvoicePage = lazy(() => import("@/pages/invoice"));
const DeliveryPage = lazy(() => import("@/pages/delivery"));
const DeliveryProfilePage = lazy(() => import("@/pages/delivery/container/DeliveryProfilePage"));
const ObjectPage = lazy(() => import("@/pages/object"));
const ObjectProfilePage = lazy(() => import("@/pages/object/container/ObjectProfilePage"));
const NotFoundPage = lazy(() => import("@/pages/notFound/NotFoundPage"));
const ObjectEmployeesPage = lazy(() => import("@/pages/objectEmployee"));
const AdminPage = lazy(() => import("@/pages/admin"));
const CashiersPage = lazy(() => import("@/pages/exchanger"));
const PaymentTypesPage = lazy(() => import("@/pages/paymentTypes"));
const CurrenciesPage = lazy(() => import("@/pages/currencies"));
const CategoriesPage = lazy(() => import("@/pages/categories"));
const RolesPage = lazy(() => import("@/pages/roles"));
const MeasuresPage = lazy(() => import("@/pages/measures"));
const ProductsPage = lazy(() => import("@/pages/products"));
const PurchasePage = lazy(() => import("@/pages/purchase"));
const WriteOffPage = lazy(() => import("@/pages/writeoff"));
const ProductReportsPage = lazy(() => import("@/pages/productReports"));
const WarehouseStockPagingPage = lazy(() => import("@/pages/product-warehouse/StockPagingPage"));
const WarehouseCostPagingPage = lazy(() => import("@/pages/product-warehouse/CostPagingPage"));
const WarehouseStockPage = lazy(() => import("@/pages/product-warehouse/StockPage"));
const WarehouseUsedPage = lazy(() => import("@/pages/product-warehouse/UsedPage"));

// ------ || Private Routes || ------ //
/**
 * Private routes for the application.
 * These routes are accessible only to authenticated users with specific roles.
 * @type {Array<RouteObject & { role: keyof IRoles_ }>}
 * @property {React.ReactNode} element - The component to render for the route.
 * @property {string} path - The path for the route.
 */

export const privateRoutes: (RouteObject & { role: keyof IRoles_ })[] = [
  // {
  //   element: <CashboxMainPage />,
  //   path: "/cashbox/main",
  //   role: "getCashbox",
  // },
  // {
  //   element: <CashboxObjectPage />,
  //   path: "/cashbox/object",
  //   role: "getCashbox",
  // },
  {
    element: <CashboxPage />,
    path: "/cashbox/main",
    role: "transaction",
  },
  {
    element: <InvoicePage />,
    path: "/cashbox/invoice",
    role: "_id",
  },
  {
    element: <DeliveryPage />,
    path: "/delivery",
    role: "supplier",
  },
  {
    element: <DeliveryProfilePage />,
    path: "/delivery/profile/:_id",
    role: "supplier",
  },
  {
    element: <ObjectPage />,
    path: "/object",
    role: "_id",
  },
  {
    element: <ObjectProfilePage />,
    path: "/object/profile/:_id",
    role: "_id",
  },
  {
    element: <ObjectEmployeesPage />,
    path: "/employees/object",
    role: "employee",
  },
  {
    element: <AdminPage />,
    path: "/employees/admin",
    role: "employee",
  },
  {
    element: <CashiersPage />,
    path: "/employees/exchanger",
    role: "employee",
  },
  {
    element: <PaymentTypesPage />,
    path: "/settings/payment-types",
    role: "payment_method",
  },
  {
    element: <CurrenciesPage />,
    path: "/settings/currencies",
    role: "currency",
  },
  {
    element: <CategoriesPage />,
    path: "/settings/categories",
    role: "transaction_type",
  },
  {
    element: <RolesPage />,
    path: "/settings/roles",
    role: "role",
  },
  {
    element: <MeasuresPage />,
    path: "/settings/measures",
    role: "unit_of_measure",
  },
  {
    element: <ProductsPage />,
    path: "/products",
    role: "product",
  },
  {
    element: <PurchasePage />,
    path: "/purchase",
    role: "purchase",
  },
  {
    element: <WriteOffPage />,
    path: "/write-off",
    role: "write_off",
  },
  // {
  //   element: <ProductReportsPage />,
  //   path: "/product-reports/pagin",
  //   role: "_id",
  // },
  {
    element: <WarehouseStockPagingPage />,
    path: "/product-warehouse/stock-products",
    role: "product_warehouse",
  },
  {
    element: <WarehouseCostPagingPage />,
    path: "/product-warehouse/cost/paging",
    role: "product_warehouse",
  },
  {
    element: <WarehouseStockPage />,
    path: "/product-warehouse/stock-list",
    role: "product_warehouse",
  },
  {
    element: <WarehouseUsedPage />,
    path: "/product-warehouse/used",
    role: "product_warehouse",
  },
  {
    element: <NotFoundPage />,
    path: "*",
    role: "_id",
  },
  {
    element: <Navigate to="/cashbox/main" replace />,
    index: true,
    role: "_id",
  },

].filter(Boolean) as (RouteObject & { role: keyof IRoles_ })[];

// ------ || Teacher Routes || ------ //
/**
 * Teacher-specific routes for the application.
 * These routes are accessible only to authenticated users with the teacher role.
 * @type {Array<RouteObject & { role: keyof IRoles_ }>}
 * @property {React.ReactNode} element - The component to render for the route.
 * @property {string} path - The path for the route.
 */

export const teacherRoutes: (RouteObject & { role: keyof IRoles_ })[] = [
  {
    element: <NotFoundPage />,
    path: "*",
    role: "_id",
  },
].filter(Boolean) as (RouteObject & { role: keyof IRoles_ })[];
